import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, List, ListItemButton, ListItemText } from 'src/components/mui';

import { useMenuContext } from '../../context';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { MenuType } from 'src/components/types';
import { extractSlug, filterMenuItems, getParentIds } from '../../utils';
import { TBurgerMenu } from './types';
import { Card, Grid } from '@mui/material';
import { hexToRGBA } from 'src/utils/all';

type Props = {
  items: TBurgerMenu[];
  view?: string;
  searchText: string;
  sendToGA: (type: string, obj: any, isMultiPaths?: boolean) => void;
  disableShouldHighlight?: boolean;
  currentSid?: string;
  activeMenuId?: number;
  sendTagToGA: (sid: string) => void;
  moveToMPLabels?: (menu: TBurgerMenu) => void;
  loadModel?: (...args: any[]) => void;
  handleItemClickLuma?: (item: TBurgerMenu) => void;
  libraryIconsBaseUrl: string;
  awsIconUrl: string;
  handleSetMenuItem?: (item: TBurgerMenu) => void;
  handleSetSubItem?: (item: TBurgerMenu) => void;
};

interface MenuItemProps {
  item: TBurgerMenu;
  level: number;
  openMenus: Record<string, boolean>;
  toggleCollapse: (id: string) => void;
  handleItemClick: (item: TBurgerMenu) => void;
  isItemHighlighted: (item: TBurgerMenu) => boolean;
  rtl: boolean;
  colors: { [key: string]: any };
  expanded: boolean;
  shouldHighlightColors: { [key: string]: any };
  libraryIconsBaseUrl?: string;
  awsIconUrl?: string;
}

const MenuItem = memo(
  ({
    item,
    level,
    openMenus,
    toggleCollapse,
    handleItemClick,
    isItemHighlighted,
    rtl,
    colors,
    expanded,
    shouldHighlightColors,
  }: MenuItemProps) => {
    const children = useMemo<TBurgerMenu[]>(() => {
      return level === 0 ? item.subMenus || [] : item.subSubMenus || [];
    }, [item, level]);
    const hasChildren = children.length > 0;

    // level === 0 — expanded, level in [1, 2] — openMenus
    const isOpen = useMemo(() => {
      return level === 0
        ? (expanded && hasChildren) || Boolean(openMenus[item.id])
        : Boolean(openMenus[item.id]);
    }, [expanded, hasChildren, openMenus, item.id, level]);

    const paddingLeft = level > 0 ? level * 4 : undefined;

    const onClick = useCallback(() => {
      if (hasChildren) {
        toggleCollapse(`${item.id}`);
      } else {
        handleItemClick(item);
      }
    }, [hasChildren, item, toggleCollapse, handleItemClick]);

    const buttonStyle: any = useMemo(
      () => ({
        paddingLeft,
        textAlign: rtl ? 'right' : 'left',
        background: hasChildren && isOpen ? colors.open : 'transparent',
        ...(isItemHighlighted(item) ? shouldHighlightColors : {}),
        '&:hover': {
          background: colors.hover,
        },
      }),
      [
        paddingLeft,
        rtl,
        hasChildren,
        isOpen,
        colors,
        isItemHighlighted,
        item,
        shouldHighlightColors,
      ],
    );

    const arrowStyle = useMemo(
      () => ({
        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 0.3s',
      }),
      [isOpen],
    );

    return (
      <Fragment>
        <ListItemButton sx={buttonStyle} onClick={onClick}>
          <ListItemText
            primary={item.title}
            sx={{
              '& .MuiListItemText-primary': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }
            }}
          />
          {hasChildren && <i className="far fa-angle-down" style={arrowStyle} />}
        </ListItemButton>
        {hasChildren && (
          <Collapse timeout={expanded ? 0 : 'auto'} in={level === 0 ? isOpen : expanded || isOpen}>
            {children.map((child) => (
              <MenuItem
                key={child.id}
                item={child}
                level={level + 1}
                openMenus={openMenus}
                toggleCollapse={toggleCollapse}
                handleItemClick={handleItemClick}
                isItemHighlighted={isItemHighlighted}
                rtl={rtl}
                colors={colors}
                expanded={expanded}
                shouldHighlightColors={shouldHighlightColors}
              />
            ))}
          </Collapse>
        )}
      </Fragment>
    );
  },
);

const MenuGridItem = memo(
  ({
    item,
    level,
    openMenus,
    toggleCollapse,
    handleItemClick,
    isItemHighlighted,
    rtl,
    colors,
    shouldHighlightColors,
     libraryIconsBaseUrl,
     awsIconUrl,
  }: MenuItemProps) => {
    const { icon, iconFile, iconColor, title, iconOpacity } = item;
    const children = useMemo<TBurgerMenu[]>(() => {
      return level === 1 ? item.subSubMenus || [] : item.subSubMenus || [];
    }, [item, level]);
    const hasChildren = children.length > 0;
    const paddingLeft = level > 0 ? level * 4 : undefined;

    const onClick = useCallback(() => {
      if (hasChildren) {
        toggleCollapse(`${item.id}`);
      } else {
        handleItemClick(item);
      }
    }, [hasChildren, item, toggleCollapse, handleItemClick]);

    const buttonStyle: any = useMemo(
      () => ({
        paddingLeft,
        display: 'block',
        textAlign: 'center',
        color: 'white',
        direction: 'column',
        px: '0px',
        background: 'transparent',
        ...(isItemHighlighted(item) ? shouldHighlightColors : {}),
        '&:hover': {
          background: 'transparent',
        },
      }),
      [
        paddingLeft,
        rtl,
        hasChildren,
        colors,
        isItemHighlighted,
        item,
        shouldHighlightColors,
      ],
    );

    const iconRGBAColor = useMemo(() =>  hexToRGBA(iconColor || "#fff", item.iconOpacity / 100), [iconOpacity, iconColor]);

    return (
      <>
        <Card sx={{ width: '130px', height: '115px', alignContent: 'center', background: iconFile ? `linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), url(${awsIconUrl}/${iconFile}) lightgray 50% / cover no-repeat` : 'rgba(255, 255, 255, 0.10)' }} onClick={onClick}>
          <ListItemButton sx={buttonStyle}>
            {icon && (
              <Box>
                <img src={`${libraryIconsBaseUrl}/${icon}?hex=${encodeURIComponent(iconRGBAColor)}`} width={32} height={32} alt={`${item.title} icon`} />
              </Box>
            )}
            <ListItemText
              primary={title}
              sx={{
                '& .MuiListItemText-primary': {
                  overflow: 'hidden',
                  color: '#FFF',
                  textAlign: 'center',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '160%', /* 22.4px */
                }
              }}
            />
          </ListItemButton>
        </Card>
      </>
    );
  },
);

export const MenuItems = ({
  items,
  view,
  sendToGA,
  searchText,
  currentSid: propsCurrentSid,
  moveToMPLabels,
  activeMenuId,
  loadModel,
  sendTagToGA,
  handleItemClickLuma,
  libraryIconsBaseUrl,
  awsIconUrl,
  handleSetMenuItem,
  handleSetSubItem,
}: Props) => {
  const { colors, settings, rtl, open } = useMenuContext();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [originMenus, setOriginMenus] = useState<TBurgerMenu[]>([]);
  const [highlightedItems, setHighlightedItems] = useState<number[]>([]);
  const [currentSid, setCurrentSid] = useState<string>();
  const [parent, enableAnimations] = useAutoAnimate();
  const clickedActiveId = useRef<number | null>(null);

  const expanded = searchText.length > 0;

  const filteredMenus = useMemo(() => filterMenuItems(items, searchText), [items, searchText]);

  const toggleCollapse = useCallback((id: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const isItemHighlighted = useCallback(
    (m: TBurgerMenu) => {
      if (highlightedItems.includes(m.id)) return true;
      if (!m?.shouldHighlight || m.type !== 'treedis' || !currentSid) return false;
      // Do not highlight if the item is a container for nested menus
      const hasNested =
        (m?.subMenus && m?.subMenus?.length > 0) || (m?.subSubMenus && m?.subSubMenus?.length > 0);
      if (hasNested) return false;
      return m.url.includes(currentSid);
    },
    [highlightedItems, currentSid],
  );

  const shouldHighlightColors = useMemo(
    () => ({
      background: colors.hover,
      color: 'var(--color-secondary) !important',
    }),
    [colors.hover],
  );

  const updateCurrentSid = useCallback(
    (sid?: string | null) => {
      if (sid && currentSid !== sid) {
        setCurrentSid(sid);
      }
    },
    [currentSid],
  );

  useEffect(() => {
    updateCurrentSid(propsCurrentSid);
  }, [propsCurrentSid, updateCurrentSid]);

  // Setting origin menus the first time because later menus may be different when clicking on a submenu
  useEffect(() => {
    if (originMenus.length === 0) {
      setOriginMenus(items);
    }
  }, [items, originMenus.length]);

  const handleOpenMenus = useCallback((menus: TBurgerMenu[]) => {
    const openIds: Record<string, boolean> = {};
    menus.forEach((menu) => {
      if (menu.subMenus && menu.subMenus.length > 0) {
        openIds[menu.id] = true;
        menu.subMenus.forEach((sub) => {
          openIds[sub.id] = true;
          if (sub.subSubMenus && sub.subSubMenus.length > 0) {
            sub.subSubMenus.forEach((subSub) => {
              openIds[subSub.id] = true;
            });
          }
        });
      }
    });
    return openIds;
  }, []);

  useEffect(() => {
    if (settings.categoryExpand) {
      const openIds = handleOpenMenus(originMenus);
      setOpenMenus(openIds);
    } else {
      setOpenMenus({});
    }
  }, [handleOpenMenus, settings.categoryExpand, originMenus]);

  // By highlightedItems changes – open parent menu to highlight  submenu item(s)
  useEffect(() => {
    const newOpenMenus = { ...openMenus };
    let changed = false;
    originMenus.forEach((menu) => {
      if (menu.subMenus && menu.subMenus.some((sub) => isItemHighlighted(sub))) {
        if (!openMenus[menu.id]) {
          newOpenMenus[menu.id] = true;
          changed = true;
        }
      }
      menu.subMenus?.forEach((sub) => {
        if (sub.subSubMenus && sub.subSubMenus.some((subSub) => isItemHighlighted(subSub))) {
          if (!openMenus[sub.id]) {
            newOpenMenus[sub.id] = true;
            changed = true;
          }
        }
      });
    });
    if (changed) {
      setOpenMenus(newOpenMenus);
    }
  }, [highlightedItems, originMenus, openMenus, isItemHighlighted]);

  // By activeMenuId changes – open parent menu and update highlightedItems
  useEffect(() => {
    if (activeMenuId && activeMenuId !== clickedActiveId.current) {
      const { menuId, subMenuId } = getParentIds(items, activeMenuId);
      if (menuId && !openMenus[menuId]) toggleCollapse(`${menuId}`);
      if (subMenuId && !openMenus[subMenuId]) toggleCollapse(`${subMenuId}`);
      setHighlightedItems((prev) => [
        ...prev.filter((i) => i !== clickedActiveId.current),
        activeMenuId,
      ]);
      clickedActiveId.current = activeMenuId;
    }
  }, [activeMenuId, items, openMenus, toggleCollapse]);

  // menu animation
  useEffect(() => {
    if (open) {
      enableAnimations(false);
      const timer = setTimeout(() => {
        enableAnimations(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [enableAnimations, open]);

  const moveToMenuItem = useCallback(
    (menu: TBurgerMenu) => {
      if (menu.type === MenuType.MatterportLabel || menu.type === MenuType.MatterportFloor) {
        moveToMPLabels?.(menu);
      } else {
        loadModel?.(menu);
      }
    },
    [moveToMPLabels, loadModel],
  );

  const resetHighlightedItems = useCallback(() => {
    setHighlightedItems([]);
    clickedActiveId.current = null;
  }, []);

  const handleItemClickDefault = useCallback(
    (item: TBurgerMenu) => {
      moveToMenuItem(item);
      if (item.type === 'treedis') {
        const url = new URL(item.url);
        const params = new URLSearchParams(url.search);
        const sid = params.get('s');
        if (sid) {
          updateCurrentSid(sid);
        } else {
          const slug = extractSlug(item.url);
          updateCurrentSid(slug);
        }
      }
      if (sendToGA) {
        sendToGA('event', {
          eventCategory: 'Place',
          eventAction: 'click',
          eventLabel: item.title,
          eventType: item.type,
        });
      }
      if (item.type === 'tag' && sendTagToGA) {
        sendTagToGA(item.tag);
      }
    },
    [moveToMenuItem, updateCurrentSid, sendToGA, sendTagToGA],
  );

  const handleItemClick = useCallback(
    (item: TBurgerMenu) => {
      resetHighlightedItems();
      if (handleItemClickLuma) {
        handleItemClickLuma(item);
      } else {
        handleItemClickDefault(item);
      }
    },
    [handleItemClickLuma, handleItemClickDefault, resetHighlightedItems],
  );

  return (
    <Box
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          width: '6px',
          backgroundColor: '#BDBDBD',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#F7F7F7',
        },
      }}
    >
      {view === 'list' ? (
        <List ref={parent}>
          {filteredMenus?.map((menuItem) => (
            <MenuItem
              key={menuItem.id}
              item={menuItem}
              level={0}
              openMenus={openMenus}
              toggleCollapse={toggleCollapse}
              handleItemClick={handleItemClick}
              isItemHighlighted={isItemHighlighted}
              rtl={rtl}
              colors={colors}
              expanded={expanded}
              shouldHighlightColors={shouldHighlightColors}
            />
          ))}
        </List>
      ) : (
        <Grid container rowSpacing={1} columnSpacing={1} px={2} ref={parent}>
          {filteredMenus?.map((menuItem) => {
            const handleGridItemClick = () => {
              if (menuItem?.subMenus?.length > 0) {
                handleSetMenuItem?.(menuItem);
              } else if (menuItem?.subSubMenus?.length > 0) {
                handleSetSubItem?.(menuItem);
              }
            };

            return (
              <Grid item xs={6} key={menuItem.id} onClick={handleGridItemClick} sx={{ cursor: 'pointer' }}>
                <MenuGridItem
                  item={menuItem}
                  libraryIconsBaseUrl={libraryIconsBaseUrl}
                  awsIconUrl={awsIconUrl}
                  level={0}
                  openMenus={openMenus}
                  toggleCollapse={toggleCollapse}
                  handleItemClick={handleItemClick}
                  isItemHighlighted={isItemHighlighted}
                  rtl={rtl}
                  colors={colors}
                  expanded={expanded}
                  shouldHighlightColors={shouldHighlightColors}
                />
              </Grid>
            )
          })}
        </Grid>
      )}
    </Box>
  );
};
